// Central API wrapper for DetailWidget endpoints.
// Vereinheitlicht: eine einzige Axios Instanz (detailWidgetAxios) statt testsAPI + productsAPI.
// Basis: /ts  -> spezifische Teilpfade in den Funktionen (/tests/*, /products/*).
// Call Style bleibt identisch zu fetchDashboards (get(...).then(...).catch(...)).

import axios from 'axios';

const BACKEND_SVC = process.env.REACT_APP_BACKEND_SVC; // same env var used in http-common.js

// Vereinheitlichte Instanz (Basis /ts); spezifische Pfade folgen in den Funktionen
const detailWidgetAPI = axios.create({
  baseURL: `${BACKEND_SVC}/ts`,
  headers: { 'Content-Type': 'application/json' }
});

// Helper to build auth header once
const authHeaders = (token) => ({ headers: { Authorization: token } });

// DETAIL FETCH
// GET /detail?productID=&testID=&widgetId=
export const fetchDetail = (token, { productID, testID, widgetId }) => {
  // Vereinheitlicht: wie die anderen Calls -> Pfad + config Objekt (params + headers)
  return detailWidgetAPI.get('/details', {
    ...authHeaders(token),
    params: {
      productID,
      testID,
      widgetId: widgetId || ''
    }
  });
};

// KLICKWEG UPDATE
// POST /klickweg/update  Body: { productID, testID, delta, userName }
export const updateKlickweg = (token, { productID, testID, delta, userName }) => {
  return detailWidgetAPI.post('/details/clickpath', { productID, testID, delta, userName }, authHeaders(token));
};

// HANDLUNGSANWEISUNG UPDATE
// POST /handlungsanweisung/update Body: { productID, testID, delta, userName }
export const updateHandlungsanweisung = (token, { productID, testID, delta, userName }) => {
  return detailWidgetAPI.post('/details/instruction', { productID, testID, delta, userName }, authHeaders(token));
};

// TEST FREQUENCY UPDATE
// POST /details/interval Body: { productID, testID, intervalMinutes, adminPassword, userName }
export const updateTestFrequency = (token, { productID, testID, intervalMinutes, adminPassword, userName }) => {
  return detailWidgetAPI.post('/details/interval', { productID, testID, intervalMinutes, adminPassword, userName }, authHeaders(token));
};

// METADATA UPDATE (Panel 1 Formular)
// POST /details/metadata Body: { productID, testID, userName, productName, testName, testOwner, thresholdOkay, thresholdWarn, thresholdCrit }
export const updateMetadata = (token, { productID, testID, userName, productName, testName, testOwner, thresholdOkay, thresholdWarn, thresholdCrit }) => {
  return detailWidgetAPI.post('/details/metadata', { productID, testID, userName, productName, testName, testOwner, thresholdOkay, thresholdWarn, thresholdCrit }, authHeaders(token));
};

// DELETE PRODUCT
// DELETE /:productID  (query userName & adminPassword if required or body if backend permits)
export const deleteProduct = (token, { productID, adminPassword, userName }) => {
  // IDs und weitere Felder im Body senden; Endpoint ohne URL-IDs
  return detailWidgetAPI.delete('/details/deleteProduct', {
    ...authHeaders(token),
    data: {
      productID,
      userName: userName || '',
      adminPassword: adminPassword || ''
    }
  });
};

// DELETE TEST
// DELETE /:productID/test/:testID  (adjust if backend chooses different structure)
export const deleteTest = (token, { productID, testID, adminPassword, userName }) => {
  // IDs und weitere Felder im Body senden; Endpoint ohne URL-IDs
  return detailWidgetAPI.delete('/details/deleteTest', {
    ...authHeaders(token),
    data: {
      productID,
      testID,
      userName: userName || '',
      adminPassword: adminPassword || ''
    }
  });
};

// NOTE: Pfade ggf. anpassen sobald Backend final ist.
// Provide a unified export for convenience.
export const detailWidgetApi = {
  fetchDetail,
  updateKlickweg,
  updateHandlungsanweisung,
  updateTestFrequency,
  updateMetadata,
  deleteProduct,
  deleteTest
};

// Example usage pattern (mirrors fetchDashboards style):
// const token = receiveIDToken();
// detailWidgetApi.fetchDetail(token, { productID, testID, widgetId })
//   .then(resp => { setDetailData(resp.data); })
//   .catch(err => { handleNotify({ type:'alert', message:'Detaildaten Fehler'}); });
